import React from "react";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

// Trustpilot brand colors
const TP_GREEN = "#00B67A";

// Trustpilot-style star: green square with white star inside
const TrustpilotStar = ({ size = 24 }) => (
  <div
    className="flex items-center justify-center"
    style={{ width: size, height: size, background: TP_GREEN }}
  >
    <svg viewBox="0 0 24 24" width={size * 0.7} height={size * 0.7} fill="white">
      <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18.2 22 12 18.3 5.8 22l1.7-7.2L2 10l7.1-1.1L12 2z" />
    </svg>
  </div>
);

const StarRow = ({ size = 22 }) => (
  <div className="flex gap-0.5">
    {[0, 1, 2, 3, 4].map((i) => (
      <TrustpilotStar key={i} size={size} />
    ))}
  </div>
);

// Official Trustpilot logo (green star + text)
const TrustpilotLogo = () => (
  <div className="flex items-center gap-1.5">
    <svg viewBox="0 0 24 24" width="18" height="18" fill={TP_GREEN}>
      <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18.2 22 12 18.3 5.8 22l1.7-7.2L2 10l7.1-1.1L12 2z" />
    </svg>
    <span className="font-semibold text-zinc-900 text-sm tracking-tight">Trustpilot</span>
  </div>
);

const Testimonials = () => {
  const { t } = useLang();
  const reviews = t.home.testimonials || [];
  const totalReviews = 237;
  const score = "4.9";

  return (
    <section className="bg-mist py-20 md:py-24" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header - Trustpilot summary strip */}
        <div className="mb-10 md:mb-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-4">
              <span className="h-px w-8 bg-zinc-400" />
              {t.home.testimonialsTag}
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 leading-[1.05] max-w-2xl">
              {t.home.testimonialsTitle}
            </h2>
          </div>

          {/* Score summary card */}
          <div className="lg:col-span-5 lg:justify-self-end w-full lg:w-auto">
            <div
              className="bg-white rounded-2xl border border-zinc-200 shadow-taxi-card p-5 md:p-6 flex items-center gap-5"
              data-testid="trustpilot-summary"
            >
              <div className="flex flex-col items-start gap-2">
                <StarRow size={28} />
                <div className="text-zinc-900 text-sm font-semibold">
                  <span style={{ color: TP_GREEN }} className="font-bold">
                    {t.home.testimonialsRatingLabel}
                  </span>{" "}
                  <span className="text-zinc-500">
                    · {score}/5 · {totalReviews} {t.home.testimonialsReviews}
                  </span>
                </div>
                <TrustpilotLogo />
              </div>
            </div>
          </div>
        </div>

        {/* Reviews grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <article
              key={i}
              data-testid={`testimonial-${i}`}
              className="bg-white rounded-2xl border border-zinc-200 shadow-taxi-card p-6 md:p-7 flex flex-col h-full hover:-translate-y-0.5 hover:shadow-taxi-lift transition-all"
            >
              {/* Top: stars + date */}
              <div className="flex items-center justify-between mb-4">
                <StarRow size={20} />
                <div className="text-zinc-400 text-xs">{r.date}</div>
              </div>

              {/* Verified badge */}
              <div className="flex items-center gap-1.5 mb-3">
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: TP_GREEN }} strokeWidth={2.5} />
                <span className="text-xs font-medium" style={{ color: TP_GREEN }}>
                  {t.home.testimonialsVerified}
                </span>
              </div>

              {/* Quote */}
              <p className="text-zinc-800 text-[15px] leading-relaxed flex-1">{r.quote}</p>

              {/* Bottom: author */}
              <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-zinc-900 text-[#FFD500] flex items-center justify-center font-display font-bold text-sm flex-shrink-0">
                  {r.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm font-semibold text-zinc-900 truncate">{r.name}</div>
                  <div className="text-zinc-500 text-xs flex items-center gap-1.5 mt-0.5">
                    <span className="text-base leading-none">{r.flag}</span>
                    <span className="truncate">{r.country}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Footer strip - based on Trustpilot */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-zinc-500">
          <span>Powered by</span>
          <TrustpilotLogo />
          <span className="hidden sm:inline text-zinc-300">·</span>
          <span>
            <span className="text-zinc-900 font-semibold">{score}/5</span> · {totalReviews} {t.home.testimonialsReviews}
          </span>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
